import { useState, useEffect, ChangeEvent } from 'react'
import { differenceBy } from 'lodash-es'
import { SortableContainer, SortableContainerProps } from 'react-sortable-hoc'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'

import { reorder } from '@/helpers/dnd'
import { Form } from '@/ui'
import { GlobalStyle, MultiOptionWrapper } from './Styles'
import Option from './Option'
import { DropdownProps } from 'semantic-ui-react'

interface SortableListProps extends SortableContainerProps {
  disabledCheckbox?: boolean
  error?: string
  items: any[]
  options?: any[]
  type?: string
  disabled?: boolean
  sortable?: boolean
  checkboxLabel?: string
  handleChange: (e: any, data: any, isPredefined?: any) => void
  handleBlur: (e: any, data: any, isPredefined?: any) => void
  handleCheckboxChange: (checked: boolean, value: string) => void
  handleDelete: (i: string) => void
  disableRemove: (i: string) => boolean
  useToggle?: boolean
  predefinedOptions?: any[]
}

const SortableList = SortableContainer<SortableListProps>(
  ({
    disabledCheckbox,
    error,
    items,
    options,
    type,
    disabled,
    sortable,
    checkboxLabel,
    handleChange,
    handleBlur,
    handleCheckboxChange,
    handleDelete,
    disableRemove,
    useToggle,
    predefinedOptions,
  }: SortableListProps) => (
    <div>
      {items.map(({ id, init, value, checked, isPredefined }, index) => {
        const filteredOptions = [...differenceBy(options, items, 'value')]

        if (value && options) {
          filteredOptions.unshift(options.find(x => x.value === value))
        }

        return (
          <Option
            key={`${id}-${index}`}
            index={index}
            init={init}
            value={value}
            error={!init && error}
            checked={checked}
            isPredefined={isPredefined}
            options={filteredOptions}
            type={type}
            disabled={!init || disabled} // disable sorting
            disabledInput={disabled}
            disabledCheckbox={disabledCheckbox}
            sortable={sortable}
            checkboxLabel={checkboxLabel}
            handleChange={(
              e: ChangeEvent<HTMLInputElement>,
              data: DropdownProps,
            ) => {
              handleChange(e?.target?.value || data?.value, index, isPredefined)
            }}
            handleBlur={(
              e: ChangeEvent<HTMLInputElement>,
              data: DropdownProps,
            ) => {
              handleBlur(e?.target?.value || data?.value, index, isPredefined)
            }}
            handleCheckboxChange={handleCheckboxChange}
            handleDelete={(i: any) => handleDelete(i)}
            disableRemove={disableRemove}
            useToggle={useToggle}
            predefinedOptions={predefinedOptions?.filter(
              option =>
                option.value === value ||
                !items.some(item => option.value === item.value),
            )}
          />
        )
      })}
    </div>
  ),
)

interface MultiOptionProps {
  options?: any[]
  type?: string
  sortable?: boolean
  disabled?: boolean
  checkboxLabel?: string
  error?: string
  disabledCheckbox?: boolean
  disableRemove?: () => boolean
  useToggle?: boolean
  onBlur: (e: any, data: any) => void
  onChange?: (e: any, data: any) => void
  onDragEnd?: (e: any, data: any) => void
  value: any[]
  predefinedOptions?: any[]
}
interface Item {
  checked?: boolean
  id?: string
  index?: number
  init?: boolean
  isPredefined?: any
  new?: boolean
  value?: string
}
const MultiOption = ({
  options,
  type,
  sortable,
  disabled,
  checkboxLabel,
  error,
  disabledCheckbox,
  disableRemove = () => false,
  useToggle,
  onBlur,
  onChange,
  onDragEnd,
  value,
  predefinedOptions,
}: MultiOptionProps) => {
  const [initialItems, setInitialItems] = useState<Item[]>([])

  const [items, setItems] = useState<Item[]>([])
  /* eslint-disable */
  useEffect(() => {
    const updateOptions = () => {
      const placeholder = initialItems.find(item => !item.init) || {}

      const newInitialItems = !disabled
        ? [
            {
              value: '',
              init: false,
              checked: false,
              new: true,
              id: placeholder.id || uuid(),
            },
          ]
        : []

      if (value.length) {
        const items = [
          ...value.map((x, index) => ({
            id: x.id || uuid(),
            init: true,
            checked: typeof x === 'object' ? x.checked : false,
            value: typeof x === 'object' ? x.value : x,
            new: x.new,
            isPredefined: x.isPredefined,
            index,
          })),
          ...newInitialItems,
        ]

        setInitialItems(items)
        setItems(items)
        return
      }

      setInitialItems(newInitialItems)
      setItems(newInitialItems)
    }
    updateOptions()
  }, [disabled, value])

  const handleCheckboxChange = (checked: boolean, value: string) => {
    const index = items.findIndex(x => x.value === value)

    items[index].checked = checked

    setItems(items)

    if (onChange) {
      onChange(null, {
        value: items.filter(x => x.init && x.value?.length),
      })
    }
  }

  const updateItems = (
    newValue: string,
    index: number,
    isPredefined: boolean,
  ) => {
    if (index !== -1) {
      items[index].index = index
      items[index].value = newValue
      items[index].init = true
      items[index].isPredefined = isPredefined

      if (!items.find(x => !x.init)) {
        items.push({
          id: uuid(),
          value: '',
          init: false,
          checked: false,
          new: true,
          index,
        })
      }

      setItems([...items])
    }

    return items.filter(x => x.init && x.value?.length)
  }

  const handleChange = (
    newValue: string,
    index: number,
    isPredefined: boolean,
  ) => {
    const value = updateItems(newValue, index ?? items.length - 1, isPredefined)

    if (onChange) {
      onChange(null, {
        value,
      })
    }
  }

  const handleBlur = (
    newValue: string,
    index: number | null,
    isPredefined: boolean,
  ) => {
    if (!newValue) {
      return
    }

    const value = updateItems(newValue, index ?? items.length - 1, isPredefined)

    if (onBlur) {
      onBlur(null, {
        value,
      })
    }
  }

  const handleDelete = (value: string) => {
    const newItems = items.filter(x => x.value !== value || !x.init)

    setItems(newItems)

    if (onBlur) {
      onBlur(null, {
        value: newItems.filter(x => x.init && x.value?.length),
      })
    }

    if (onChange) {
      onChange(null, {
        value: newItems.filter(x => x.init && x.value?.length),
      })
    }
  }

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    const newItems = reorder(items, oldIndex, newIndex).map((item, index) => ({
      ...item,
      index,
    }))

    setItems(newItems)
    if (onDragEnd) {
      return onDragEnd(null, {
        value: newItems.filter(x => x.init && x.value.length),
      })
    }
    if (onBlur) {
      onBlur(null, {
        value: newItems.filter(x => x.init && x.value.length),
      })
    }

    if (onChange) {
      onChange(null, {
        value: newItems.filter(x => x.init && x.value.length),
      })
    }
  }

  return (
    <MultiOptionWrapper>
      <GlobalStyle />

      <SortableList
        useDragHandle
        lockToContainerEdges
        useWindowAsScrollContainer
        lockAxis="y"
        helperClass="top"
        error={error}
        disabledCheckbox={disabledCheckbox}
        items={items}
        options={options}
        type={type}
        disabled={disabled}
        sortable={sortable}
        checkboxLabel={checkboxLabel}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleCheckboxChange={handleCheckboxChange}
        handleDelete={handleDelete}
        onSortEnd={onSortEnd}
        disableRemove={disableRemove}
        useToggle={useToggle}
        predefinedOptions={predefinedOptions}
      />

      {predefinedOptions && (
        <PredefinedOptions
          options={predefinedOptions.filter(
            ({ value }) => !items.some(item => value === item.value),
          )}
          handleBlur={handleBlur}
        />
      )}
    </MultiOptionWrapper>
  )
}

interface PredefinedOptionsProps {
  options: any[]
  handleBlur: (
    newValue: string,
    index: number | null,
    isPredefined: boolean,
  ) => void
}

const PredefinedOptions = ({
  handleBlur,
  options = [],
}: PredefinedOptionsProps) => {
  const { t } = useTranslation()

  return (
    <Form.Select
      allowAdditions
      search
      value={undefined}
      placeholder={t('Type or pick competence')}
      options={options}
      onChange={(e, { value, ...rest }) =>
        handleBlur(
          value as string,
          null,
          /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/.test(
            value as string,
          ),
        )
      }
    />
  )
}

export default MultiOption
